import { CockpitPanelIntlKeysEnum, PermissionConfig, permissionsConfig } from '@components/cockpit/cockpit.types';
import {
  groupsAdapter,
  profilesAdapter,
  rolesAdapter,
  sitesAdapter,
  usersAdapter,
} from '@redux/authent/authent.adapters';
import { selectAllOrganizations } from '@redux/config/config.selectors';
import { createSelector, EntityId } from '@reduxjs/toolkit';
import { isRestrictedProfile } from '@utils/authent/profiles.utils';
import { getRoleNameAction, getRoleNameFamily, isSensorRole } from '@utils/authent/roles.utils';
import { selectParameter, stringPropertyComparator } from '@utils/common.utils';
import { CategoryEnum } from '@utils/sensors/configuration.constants';
import { denormalize } from 'normalizr';

import { DenormalizedProfile, Group, GroupLight, NormalizedGroup, Profile } from '@/types/authent/groups.types';
import { LogoutCauseEnum, NormalizedUser, SafetyKeyLight, User } from '@/types/authent/login.types';
import { Role, RoleEnum } from '@/types/authent/roles.types';
import { ActionEnum } from '@/types/commons/commons.types';
import { Organization } from '@/types/config/config.types';
import { SensorAction, SensorFamilyEnum } from '@/types/sensor/sensor.types';

import { RootState } from '../store';
import {
  selectActiveGroup,
  selectActiveOrganization,
  selectActiveUser,
  selectAuthentState,
} from './authent.base.selectors';
import { AuthentState } from './authent.reducer';
import { fullProfileSchema, userSchema } from './authent.schema';

export { selectActiveGroup, selectActiveOrganization, selectActiveUser, selectAuthentState };

export const selectToken = createSelector(selectAuthentState, (state: AuthentState): string | null => state.token);

export const selectRenewalToken = createSelector(
  selectAuthentState,
  (state: AuthentState): string | null => state.renewalToken,
);

export const selectSessionUuid = createSelector(
  selectAuthentState,
  (state: AuthentState): string | null => state.sessionUuid,
);

export const selectHasBeenInitialized = createSelector(
  selectAuthentState,
  (state: AuthentState): boolean => state.hasBeenInitialized,
);

export const selectHasSelectedGroup = createSelector(
  selectAuthentState,
  (state: AuthentState): boolean => state.hasSelectedGroup,
);

export const selectIsChangingShift = createSelector(
  selectAuthentState,
  (state: AuthentState): boolean => state.isChangingShift,
);

export const selectForcedLogoutCause = createSelector(
  selectAuthentState,
  (state: AuthentState): LogoutCauseEnum => state.forcedLogoutCause,
);

export const selectActiveUserLogin = createSelector(selectActiveUser, (state: User): string => state.login);

export const selectActiveSite = createSelector(selectActiveUser, (state: User) => state.activeSite);

export const selectUserSites = createSelector(selectActiveUser, (state: User) => state.sites);

export const selectUserGroups = createSelector(selectActiveUser, (state: User): GroupLight[] => state.groups);

export const selectActiveGroupOrganization = createSelector(
  [selectActiveGroup, (state) => selectAllOrganizations(state)],
  (group, organizations): Organization | null =>
    organizations.find((organization) => organization.code === group?.organization) ?? null,
);

export const selectActiveUserSafetyKey = createSelector(
  selectActiveUser,
  (state: User): SafetyKeyLight | null => state.safetyKey,
);

export const selectActiveProfile = createSelector(
  selectActiveGroup,
  (state: Group | null): Profile | null => state?.profile ?? null,
);

export const selectRolesFromActiveProfile = createSelector(selectActiveProfile, (profile: Profile | null) => {
  if (profile === null) {
    return null;
  }
  return new Set(profile.roles);
});

export const hasAnyRoles = createSelector(
  [selectRolesFromActiveProfile, selectParameter<RoleEnum[]>],
  (roleSet: Set<RoleEnum> | null, roles): boolean => {
    if (roleSet === null) {
      return false;
    }
    return roles.some((role) => roleSet.has(role));
  },
);

export const hasRoles = createSelector(
  [selectRolesFromActiveProfile, selectParameter<RoleEnum[]>],
  (roleSet: Set<RoleEnum> | null, roles: RoleEnum[]) => {
    if (roleSet === null) {
      return null;
    }
    return new Set(roles.filter((role) => roleSet.has(role)));
  },
);

export const hasRole = createSelector(
  [selectRolesFromActiveProfile, selectParameter<RoleEnum | undefined>],
  (roleSet: Set<RoleEnum> | null, role: RoleEnum | undefined) => {
    if (roleSet === null || role === undefined) {
      return false;
    }
    return roleSet.has(role);
  },
);

const { selectAll: selectAllRoles, selectEntities: selectRoleEntities } = rolesAdapter.getSelectors(
  (state: RootState) => state.authent.roles,
);

export const RoleSelectors = {
  selectAllRoles,
};

export const selectRolesByNames = createSelector(
  [selectAllRoles, selectParameter<RoleEnum[]>],
  (roles, roleNames): Role[] => roles.filter((role) => roleNames.includes(role.roleName)),
);

export const { selectAll: selectAllProfiles, selectEntities: selectProfileEntities } = profilesAdapter.getSelectors(
  (state: RootState) => state.authent.profiles,
);

export const selectProfileById = profilesAdapter.getSelectors((state: RootState) => state.authent.profiles)
  .selectById as (state: RootState, id: EntityId) => Profile | undefined;

const { selectEntities: selectSiteEntities } = sitesAdapter.getSelectors((state: RootState) => state.authent.sites);

const { selectAll: selectAllUsers, selectEntities: selectUserEntities } = usersAdapter.getSelectors(
  (state: RootState) => state.authent.users,
);

const selectUserById = usersAdapter.getSelectors((state: RootState) => state.authent.users).selectById as (
  state: RootState,
  id: EntityId,
) => NormalizedUser | undefined;

export const { selectAll: selectAllGroups, selectEntities: selectGroupEntities } = groupsAdapter.getSelectors(
  (state: RootState) => state.authent.groups,
);

export const selectGroupById = groupsAdapter.getSelectors((state: RootState) => state.authent.groups).selectById as (
  state: RootState,
  id: EntityId,
) => NormalizedGroup | undefined;

const selectEntities = createSelector(
  [selectUserEntities, selectProfileEntities, selectRoleEntities, selectSiteEntities, selectGroupEntities],
  (users, profiles, roles, sites, groups) => ({
    users,
    profiles,
    roles,
    sites,
    groups,
  }),
);

export const selectDenormalizedUser = createSelector(
  [selectEntities, selectUserById],
  (entities, user): User | null => denormalize(user?.id, userSchema, entities) ?? null,
);

export const selectDenormalizedUsers = createSelector([selectEntities, selectAllUsers], (entities, users): User[] =>
  users.map((user) => denormalize(user.id, userSchema, entities)),
);

export const selectProfilesWithoutRestricted = createSelector(selectAllProfiles, (profiles): Profile[] =>
  profiles.filter((profile) => !isRestrictedProfile(profile)),
);

export const selectDenormalizedProfiles = createSelector(
  [selectEntities, selectAllProfiles],
  (entities, profiles): DenormalizedProfile[] =>
    profiles.map((profile) => {
      const denormalizedProfile = denormalize(profile.id, fullProfileSchema, entities) as DenormalizedProfile;
      return { ...denormalizedProfile, roles: denormalizedProfile.roles.filter(Boolean) };
    }),
);

export const selectActiveDenormalizedProfile = createSelector(
  [selectActiveProfile, selectDenormalizedProfiles],
  (profile, profiles): DenormalizedProfile | null => {
    if (profile === null) {
      return null;
    }
    return profiles.find((p) => p.id === profile.id) ?? null;
  },
);

export const selectSensorRoleActionsFromActiveProfile = createSelector(
  [selectActiveProfile],
  (profile): Set<SensorAction> => {
    if (!profile) {
      return new Set();
    }
    return new Set(
      profile.roles.filter(isSensorRole).map((role) => ({
        action: getRoleNameAction(role)!,
        sensorFamily: getRoleNameFamily(role)!,
      })),
    );
  },
);

export const hasAnySensorRoleFromActiveProfile = createSelector(
  [selectSensorRoleActionsFromActiveProfile, selectParameter<SensorAction[]>],
  (userSensorActionSet: Set<SensorAction>, providedSensorActions: SensorAction[]): boolean => {
    return Array.from(userSensorActionSet).some((usa) =>
      providedSensorActions.some((psa) => usa.action === psa.action && usa.sensorFamily === psa.sensorFamily),
    );
  },
);

export const hasSensorRoleFromActiveProfile = createSelector(
  [selectSensorRoleActionsFromActiveProfile, selectParameter<Partial<SensorAction>>],
  (userSensorActionSet: Set<SensorAction>, providedSensorAction: Partial<SensorAction>): boolean => {
    return Array.from(userSensorActionSet).some(
      (s) => s.action === providedSensorAction.action && s.sensorFamily === providedSensorAction.sensorFamily,
    );
  },
);

export const hasSensorLADorPerimeterAccessFromActiveProfile = createSelector(
  [selectSensorRoleActionsFromActiveProfile, selectParameter<CategoryEnum>],
  (sensorActionSet: Set<SensorAction>, type: CategoryEnum): boolean => {
    return Array.from(sensorActionSet)
      .filter(
        (s) =>
          (type === CategoryEnum.LAD && s.sensorFamily !== SensorFamilyEnum.AUTOMATON) ||
          (type !== CategoryEnum.LAD && s.sensorFamily === SensorFamilyEnum.AUTOMATON),
      )
      .some((s) => s.action === ActionEnum.ACCESS);
  },
);

export const hasActionRoleOnSensors = createSelector(
  [selectSensorRoleActionsFromActiveProfile, selectParameter<ActionEnum>],
  (sensorActionSet: Set<SensorAction>, action: ActionEnum): boolean => {
    return Array.from(sensorActionSet).some((sensorAction) => sensorAction.action === action);
  },
);

export const hasAnyActionRoleOnSensors = createSelector(
  [selectSensorRoleActionsFromActiveProfile, selectParameter<ActionEnum[]>],
  (userSensorActionSet: Set<SensorAction>, providedSensorActions: ActionEnum[]): boolean => {
    return Array.from(userSensorActionSet).some((userSensorAction) =>
      providedSensorActions.some((providedSensorAction) => providedSensorAction === userSensorAction.action),
    );
  },
);

export const selectDenormalizedProfilesWithoutRestricted = createSelector(
  selectDenormalizedProfiles,
  (profiles): DenormalizedProfile[] =>
    profiles.filter((p) => !isRestrictedProfile(p)).toSorted(stringPropertyComparator('name')),
);

export const selectDenormalizedProfilesId = createSelector(
  selectDenormalizedProfiles,
  (profiles): Set<number> => new Set(profiles.map((profile) => profile.id)),
);

export const selectProfilesIdWithoutRestricted = createSelector(
  selectDenormalizedProfilesWithoutRestricted,
  (profiles): Set<number> => new Set(profiles.map((profile) => profile.id)),
);

export const selectAllProfilesWithoutRestricted = createSelector(
  [selectAllProfiles, selectProfilesIdWithoutRestricted],
  (profiles, profilesIdWithoutRestricted): Profile[] =>
    profiles.filter((profile) => profilesIdWithoutRestricted.has(profile.id)),
);

export const selectDenormalizedProfileById = createSelector(
  [selectDenormalizedProfilesWithoutRestricted, selectParameter<number>],
  (profiles: DenormalizedProfile[], id: number) => {
    return profiles.find((profile) => profile.id === id);
  },
);

export const selectPermissions = createSelector(
  [(state: RootState) => state],
  (state): Record<CockpitPanelIntlKeysEnum, boolean> => {
    return (Object.entries(permissionsConfig) as [CockpitPanelIntlKeysEnum, PermissionConfig][]).reduce(
      (result, [key, config]) => {
        switch (config.type) {
          case 'roles':
            result[key] = hasAnyRoles(state, config.roles);
            break;

          case 'roleSingle':
            result[key] = hasRole(state, config.role);
            break;

          case 'actionSensor':
            result[key] = hasAnyActionRoleOnSensors(state, config.roles);
            break;
        }

        return result;
      },
      {} as Record<CockpitPanelIntlKeysEnum, boolean>,
    );
  },
);
